import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Input,
  Select,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useRef, useEffect, useState } from "react";
import { schema } from "./add-new-charge-station.schema";
import { FormValues } from "./add-new-charge-station.type";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGetListLocationQuery } from "src/hook/location/location";
import { useCreateChargeStationMutation } from "src/hook/charge-station/charge-station";
import {
  getToastAttribute,
  PaginationType,
  ToastAttribute,
} from "src/constants";
import { useGetListCompanyQuery } from "src/hook/company/company";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  setPageIndex: any;
  userDetailData?: any;
}

const AddNewChargeStation = (props: Props) => {
  const { isOpen, onClose, setPageIndex, userDetailData } = props;
  const [listLocation, setListLocation] = useState<any>([]);
  const cancelRef = useRef();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      isSimulator: "Simulator",
      role: userDetailData?.data?.role?.id,
      company: { id: "" },
    },
    resolver: yupResolver(schema),
  });

  const companyId = useWatch({ control, name: "company.id" })
  const toast = useToast();
  const showToast = (props: ToastAttribute) => {
    toast(getToastAttribute(props));
  };
  const [createChargeStation, { isLoading }] = useCreateChargeStationMutation();
  const { data } = useGetListLocationQuery({
    page: "full",
    take: "full",
    companyId: companyId && `companyId[]=${companyId}`
  })
  const { data: companies } =
    useGetListCompanyQuery();

  const handleOnSubmit = (data: FormValues) => {
    let newData: any;
    const { company, ...rest } = data;
    if (data.isSimulator === "Simulator") {
      newData = { ...rest, isSimulator: true };
    } else {
      newData = { ...rest, isSimulator: false };
    }

    createChargeStation(newData)
      .unwrap()
      .then(() => {
        showToast({
          title: "Charge Station created.",
          dis: "Charge Station is successfully created.",
        });
        reset();
        setPageIndex((pre: PaginationType) => ({ ...pre, page: 1 }));
        onClose();
      })
      .catch((err) => {
        showToast({
          title: "Charge Station creation.",
          dis: err?.data?.message,
          sts: "error",
        });
      });
  };

  useEffect(() => {
    setListLocation(data?.data?.data ? data?.data?.data : []);
  }, [data]);

  return (
    <>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef as any}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay bg={"#052A6E"} opacity={"0.7 !important"} />
        <AlertDialogContent
          maxH={"83%"}
          minW={"56%"}
          color={"#171E38"}
          bg={"#F2F5FC"}
          m={0}
          overflowY={"auto"}
        >
          <AlertDialogCloseButton
            _focusVisible={{
              outline: "none",
              border: "none",
            }}
            size={"lg"}
          />
          <AlertDialogBody pt={"38px"} pl={"40px"}>
            <Text
              fontSize={"40px"}
              fontFamily={"Avenir Black"}
              mb={"39px"}
              w={"fit-content"}
            >
              Add charge station
            </Text>
            <form onSubmit={handleSubmit(handleOnSubmit)}>
              {userDetailData?.data?.role?.id === 1 &&
                <Box display={"flex"} mb={"24px"} alignItems={"center"}>
                  <Text fontFamily={"Avenir Heavy"} fontSize={"18px"} w={"25%"}>
                    Company name<span style={{ color: "#E74D3D" }}>*</span>
                  </Text>
                  <Box w={"60%"}>
                    <Select
                      border={"0px"}
                      bg={"white"}
                      borderRadius={"10px"}
                      variant={"base"}
                      size={"lg"}
                      textTransform={"capitalize"}
                      height={"45px"}
                      fontFamily={"Avenir Book"}
                      fontSize={"18px"}
                      {...register("company.id", {
                        shouldUnregister: true,
                      })}
                    >
                      {companies?.data?.map((item) => (
                        item?.companyName && <option key={item?.id} value={item?.id}>{item?.companyName}</option>
                      ))}
                    </Select>
                    {errors.company && (
                      <Text
                        fontSize={"12px"}
                        mt={"4px"}
                        ml={"4px"}
                        color={"red.400"}
                        fontFamily={"Avenir Medium"}
                      >
                        {errors.company.message}
                      </Text>
                    )}
                  </Box>
                </Box>}
              <Box display={"flex"} mb={"24px"} alignItems={"center"}>
                <Text fontFamily={"Avenir Heavy"} fontSize={"18px"} w={"25%"}>
                  Charge station type<span style={{ color: "#E74D3D" }}>*</span>
                </Text>
                <Select
                  w={"60%"}
                  border={"0px"}
                  bg={"white"}
                  borderRadius={"10px"}
                  variant={"base"}
                  size={"lg"}
                  textTransform={"capitalize"}
                  height={"45px"}
                  fontFamily={"Avenir Book"}
                  fontSize={"18px"}
                  {...register("isSimulator", {
                    shouldUnregister: true,
                  })}
                >
                  <option>Simulator</option>
                  <option>Physical</option>
                </Select>
              </Box>

              <Box display={"flex"} mb={"24px"} alignItems={"center"}>
                <Text fontFamily={"Avenir Heavy"} fontSize={"18px"} w={"25%"}>
                  Charge station security
                  <span style={{ color: "#E74D3D" }}>*</span>
                </Text>
                <Select
                  value={"None"}
                  w={"60%"}
                  border={"0px"}
                  bg={"white"}
                  borderRadius={"10px"}
                  variant={"base"}
                  size={"lg"}
                  textTransform={"capitalize"}
                  height={"45px"}
                  fontFamily={"Avenir Book"}
                  fontSize={"18px"}
                  isDisabled
                >
                  <option>None</option>
                </Select>
              </Box>

              <Box display={"flex"} mb={"24px"} alignItems={"center"}>
                <Text fontFamily={"Avenir Heavy"} fontSize={"18px"} w={"25%"}>
                  Location<span style={{ color: "#E74D3D" }}>*</span>
                </Text>
                <Flex flexDirection={"column"} w={"60%"}>
                  <Select
                    w={"100%"}
                    border={"0px"}
                    bg={"white"}
                    borderRadius={"10px"}
                    variant={"base"}
                    size={"lg"}
                    height={"45px"}
                    fontFamily={"Avenir Book"}
                    fontSize={"18px"}
                    {...register("location.id", {
                      shouldUnregister: true,
                    })}
                  >
                    <option>Select a location</option>
                    {listLocation.map((item) => (
                      <option key={item?.id} value={item?.id}>
                        {item?.name}
                      </option>
                    ))}
                  </Select>
                  {errors.location?.id && (
                    <Text
                      fontSize={"12px"}
                      mt={"4px"}
                      ml={"4px"}
                      color={"red.400"}
                      fontFamily={"Avenir Medium"}
                    >
                      {errors.location?.id.message}
                    </Text>
                  )}
                </Flex>
              </Box>

              <Box display={"flex"} mb={"24px"} alignItems={"center"}>
                <Text fontFamily={"Avenir Heavy"} fontSize={"18px"} w={"25%"}>
                  Name<span style={{ color: "#E74D3D" }}>*</span>
                </Text>
                <Flex flexDirection={"column"} w={"60%"}>
                  <Input
                    w={"100%"}
                    border={"0px"}
                    bg={"white"}
                    borderRadius={"10px"}
                    height={"45px"}
                    fontFamily={"Avenir Book"}
                    maxLength={100}
                    fontSize={"18px"}
                    {...register("name", {
                      shouldUnregister: true,
                    })}
                  />
                  {errors.name && (
                    <Text
                      fontSize={"12px"}
                      mt={"4px"}
                      ml={"4px"}
                      color={"red.400"}
                      fontFamily={"Avenir Medium"}
                    >
                      {errors.name.message}
                    </Text>
                  )}
                </Flex>
              </Box>

              <Box display={"flex"} mb={"24px"} alignItems={"center"}>
                <Text fontFamily={"Avenir Heavy"} fontSize={"18px"} w={"25%"}>
                  Notes
                </Text>
                <Input
                  w={"60%"}
                  border={"0px"}
                  bg={"white"}
                  borderRadius={"10px"}
                  height={"45px"}
                  fontFamily={"Avenir Book"}
                  fontSize={"18px"}
                  {...register("description", {
                    shouldUnregister: true,
                  })}
                />
              </Box>

              <Box
                mt={"40px"}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
                fontFamily={"Avenir Black"}
                mb={"25px"}
              >
                <Button
                  variant={"outline"}
                  borderColor={"#020E20"}
                  w={"191px"}
                  height={"55px"}
                  borderRadius={"37px"}
                  mr={"16px"}
                  fontSize={"20px"}
                  _hover={{
                    bg: "#BEC3C7",
                    opacity: 0.8,
                  }}
                  onClick={() => reset()}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant={"solid"}
                  bg={"#0A47BA"}
                  w={"191px"}
                  height={"55px"}
                  borderRadius={"37px"}
                  mr={"16px"}
                  color={"#FFFFFF"}
                  fontSize={"20px"}
                  isDisabled={isLoading}
                  _hover={{
                    bg: "#0A47BA",
                    opacity: 0.8,
                  }}
                  isLoading={isLoading}
                >
                  Create
                </Button>
              </Box>
            </form>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AddNewChargeStation;
